/**
 * Подсказка легенды по наведению: что за зона под курсором.
 *
 * Смысл в том, чтобы не заставлять человека держать в голове два десятка
 * цветов. Легенда во вкладке остаётся, но читать карту удобнее, когда
 * расшифровка приходит к курсору сама.
 *
 * Подсказка намеренно скромная: название зоны и, если помещается, первая фраза
 * описания. Полное описание — в карточке по клику. Всплывающее окно, которое
 * закрывает собой чертёж, мешает больше, чем помогает.
 */

import { el } from "./dom";

/** Отступ от курсора, чтобы подсказка не оказалась прямо под остриём. */
const OFFSET = 16;

export interface HoverHint {
  root: HTMLElement;
  show: (title: string, detail: string | null, x: number, y: number) => void;
  hide: () => void;
}

export function createHoverHint(container: HTMLElement): HoverHint {
  const titleNode = el("span", { class: "hint__title" });
  const detailNode = el("span", { class: "hint__detail" });
  const root = el(
    "div",
    {
      class: "hint",
      attrs: { hidden: true, role: "tooltip", "aria-live": "polite" },
    },
    titleNode,
    detailNode,
  );
  container.appendChild(root);

  let visible = false;

  const place = (x: number, y: number): void => {
    // Подсказка не должна вылезать за край карты: у правого края она
    // переезжает влево от курсора, у нижнего — вверх. Иначе у границы окна
    // она либо обрезается, либо тянет за собой полосу прокрутки.
    const box = container.getBoundingClientRect();
    const width = root.offsetWidth;
    const height = root.offsetHeight;

    let left = x + OFFSET;
    if (left + width > box.width) left = Math.max(0, x - OFFSET - width);

    let top = y + OFFSET;
    if (top + height > box.height) top = Math.max(0, y - OFFSET - height);

    root.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
  };

  return {
    root,
    show(title, detail, x, y) {
      titleNode.textContent = title;
      detailNode.textContent = detail ?? "";
      detailNode.hidden = !detail;
      if (!visible) {
        root.hidden = false;
        visible = true;
      }
      place(x, y);
    },
    hide() {
      if (!visible) return;
      root.hidden = true;
      visible = false;
    },
  };
}

/**
 * Первая фраза описания зоны — для подсказки.
 *
 * Описания в таблице зон — это официальные формулировки из легенды, по
 * нескольку строк каждая. Целиком они в подсказку не годятся: она перестаёт
 * быть подсказкой и превращается в окно поверх карты.
 */
export function firstSentence(text: string, limit = 110): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const stop = trimmed.indexOf(". ");
  const candidate = stop > 0 ? trimmed.slice(0, stop + 1) : trimmed;
  if (candidate.length <= limit) return candidate;
  const cut = candidate.lastIndexOf(" ", limit);
  return `${candidate.slice(0, cut > 40 ? cut : limit)}…`;
}
